import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanActivateFn, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { AuthorizationService } from '../../services/authorization.service';
import { DomainStateService } from '../services/domain-state.service';
import { fetchAuthSession } from 'aws-amplify/auth';

@Injectable({ providedIn: 'root' })
export class AuthGuard implements CanActivate {

  constructor(
    private readonly authorizationService: AuthorizationService,
    private readonly router: Router
  ) {}

  async canActivate(): Promise<boolean | UrlTree> {

    const token = await this.authorizationService.getTokenFromSession();

    if (token && !this.authorizationService.isTokenExpired(token)) {

      this.authorizationService.setUserRoleFromToken(token);
      return true;
    }

    return this.router.createUrlTree(['/auth/login']);
  }
}


/** Accessibile solo ai super admin. Redirige alla dashboard per tutti gli altri. */
export const superAdminGuard: CanActivateFn = () => {
  const domainStateService = inject(DomainStateService);
  const router = inject(Router);
  return domainStateService.isSuperAdmin || router.createUrlTree(['/dashboard']);
};

/** Accessibile ad admin e super admin. Redirige alla dashboard per i semplici user. */
export const atLeastAdminGuard: CanActivateFn = () => {
  const domainStateService = inject(DomainStateService);
  const router = inject(Router);
  return domainStateService.isAtLeastAdmin || router.createUrlTree(['/dashboard']);
};

@Injectable({ providedIn: 'root' })
export class NoAuthGuard implements CanActivate {

  constructor(
    private readonly authorizationService: AuthorizationService,
    private readonly router: Router
  ) {}

async canActivate(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
  if (state.url.startsWith('/auth/reset-password')) {
    return true;
  }

  try {
    const timeout = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('fetchAuthSession timeout')), 5000)
    );
    const session = await Promise.race([fetchAuthSession(), timeout]);

    const token = session.tokens?.idToken?.toString();
    if (token && !this.authorizationService.isTokenExpired(token)) {
      return this.router.createUrlTree(['/dashboard']);
    }
    return true;

  } catch {
    return true;
  }
}
}
